import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, qs, type Client, type ClientDetail, type Consultant, type Contact, type ContactPurpose, type Lookup, type Paged } from '../api';
import { ActiveBadge, DemoBadge, Dialog, Empty, ErrorAlert, Field, fieldError, Loading } from '../components/ui';
import { documentMask, phoneMask, PROJECT_STATUS_LABEL, PURPOSE_LABEL } from '../format';
import { useMe } from '../auth';

export function CadastrosPage() {
  const items = [
    { to: '/cadastros/clientes', title: 'Clientes e contatos', text: 'Clientes, documentos e destinatários de agenda, relatórios e extratos.' },
    { to: '/cadastros/consultores', title: 'Consultores', text: 'Sócios e futuros consultores. Contas de acesso ficam em Configurações.' },
    { to: '/cadastros/fases', title: 'Fases de projeto', text: 'Colunas do Kanban de projetos: nome, ordem e cor de cada fase.' },
    { to: '/cadastros/categorias', title: 'Categorias', text: 'Tipos de serviço usados nas execuções e indicadores.' },
    { to: '/cadastros/areas', title: 'Módulos/áreas', text: 'Financeiro, Suprimentos, Comercial e outras áreas, inclusive fora do Sienge.' },
    { to: '/cadastros/tipos-compromisso', title: 'Tipos de compromisso', text: 'Consultoria, treinamento, atividade interna, pessoal…' },
    { to: '/cadastros/modelos-mensagem', title: 'Modelos de mensagem', text: 'Confirmação, lembrete, reagendamento, cancelamento, relatório e extrato.' },
  ];
  return (
    <>
      <div className="page-head">
        <h1>Cadastros</h1>
      </div>
      <div className="grid grid-2">
        {items.map((i) => (
          <Link key={i.to} to={i.to} className="list-item">
            <div className="title">{i.title}</div>
            <div className="meta">{i.text}</div>
          </Link>
        ))}
      </div>
      <p className="small muted" style={{ marginTop: '1rem' }}>
        Projetos e contratos ficam no menu <Link to="/projetos">Projetos</Link>.
      </p>
    </>
  );
}

// ------------------------------------------------------------------ clientes

export function ClientsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<'true' | 'false' | 'all'>('true');
  const q = useQuery({
    queryKey: ['clients', search, active],
    queryFn: () => api.get<Paged<Client>>(`/clients${qs({ q: search, active, pageSize: 100 })}`),
    placeholderData: (prev) => prev,
  });
  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/cadastros">← Cadastros</Link>
          </p>
          <h1>Clientes</h1>
        </div>
        <div className="actions">
          <Link className="btn btn-primary" to="/cadastros/clientes/novo">
            Novo cliente
          </Link>
        </div>
      </div>
      <FinanceClientsNotice />
      <div className="toolbar">
        <label className="visually-hidden" htmlFor="c-search">Pesquisar</label>
        <input id="c-search" type="search" placeholder="Nome, fantasia, código ou CPF/CNPJ" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="segmented" role="group" aria-label="Situação">
          <button aria-pressed={active === 'true'} onClick={() => setActive('true')}>
            Ativos
          </button>
          <button aria-pressed={active === 'false'} onClick={() => setActive('false')}>
            Inativos
          </button>
          <button aria-pressed={active === 'all'} onClick={() => setActive('all')}>
            Todos
          </button>
        </div>
      </div>
      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : q.data?.data.length === 0 ? (
        <Empty>Nenhum cliente encontrado.</Empty>
      ) : (
        <>
          <div className="card hide-mobile" style={{ padding: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Código</th>
                  <th>CPF/CNPJ</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {q.data?.data.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => navigate(`/cadastros/clientes/${c.id}`)}>
                    <td>
                      <Link to={`/cadastros/clientes/${c.id}`} onClick={(e) => e.stopPropagation()}>
                        {c.tradeName || c.name}
                      </Link>{' '}
                      <DemoBadge show={c.isDemo} />
                      {c.tradeName && <div className="small muted">{c.name}</div>}
                    </td>
                    <td>{c.code}</td>
                    <td>{documentMask(c.documentType, c.document)}</td>
                    <td>
                      <ActiveBadge active={c.active} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="list show-mobile">
            {q.data?.data.map((c) => (
              <Link key={c.id} to={`/cadastros/clientes/${c.id}`} className="list-item">
                <div className="title">{c.tradeName || c.name}</div>
                <div className="meta">
                  <span>{c.code}</span>
                  <span>{documentMask(c.documentType, c.document)}</span>
                  <ActiveBadge active={c.active} />
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  );
}

export function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [contactDialog, setContactDialog] = useState<Contact | 'new' | null>(null);
  const q = useQuery({ queryKey: ['client', id], queryFn: () => api.get<ClientDetail>(`/clients/${id}`) });
  const del = useMutation({
    mutationFn: () => api.del(`/clients/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      navigate('/cadastros/clientes');
    },
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorAlert error={q.error} />;
  const c = q.data!;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/cadastros/clientes">← Clientes</Link>
          </p>
          <h1>
            {c.tradeName || c.name} <DemoBadge show={c.isDemo} />
          </h1>
          <p>
            {c.code} · <ActiveBadge active={c.active} />
          </p>
        </div>
        <div className="actions">
          <Link className="btn" to={`/cadastros/clientes/${c.id}/editar`}>
            Editar
          </Link>
          <button
            className="btn btn-danger"
            disabled={del.isPending}
            onClick={() => window.confirm('Excluir definitivamente este cliente? Só é possível se não houver projetos nem contatos.') && del.mutate()}
          >
            Excluir
          </button>
        </div>
      </div>
      <ErrorAlert error={del.error} title="Não foi possível excluir." />
      <div className="grid grid-2">
        <section className="card">
          <h2>Dados</h2>
          <dl className="props">
            <dt>Razão social / nome</dt>
            <dd>{c.name}</dd>
            <dt>Nome fantasia</dt>
            <dd>{c.tradeName || '—'}</dd>
            <dt>{c.documentType === 'cpf' ? 'CPF' : 'CNPJ'}</dt>
            <dd>{documentMask(c.documentType, c.document)}</dd>
            <dt>Endereço</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{c.address || '—'}</dd>
            <dt>Observações</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{c.notes || '—'}</dd>
          </dl>
        </section>
        <section className="card">
          <div className="card-head">
            <h2>Projetos</h2>
            <Link className="btn btn-sm" to="/projetos/novo">
              Novo projeto
            </Link>
          </div>
          {c.projects.length === 0 ? (
            <Empty>Nenhum projeto.</Empty>
          ) : (
            <div className="list">
              {c.projects.map((p) => (
                <Link key={p.id} to={`/projetos/${p.id}`} className="list-item">
                  <div className="title">{p.name}</div>
                  <div className="meta">
                    <span>{p.code}</span>
                    <span>{PROJECT_STATUS_LABEL[p.status]}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
      <section className="card">
        <div className="card-head">
          <h2>Contatos</h2>
          <button className="btn btn-sm btn-primary" onClick={() => setContactDialog('new')}>
            Novo contato
          </button>
        </div>
        {c.contacts.length === 0 ? (
          <Empty>Nenhum contato. Cadastre os destinatários de agenda, relatórios e extratos.</Empty>
        ) : (
          <div className="list">
            {c.contacts.map((ct) => (
              <button key={ct.id} className="list-item" style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }} onClick={() => setContactDialog(ct)}>
                <div className="title">
                  {ct.name} {!ct.active && <ActiveBadge active={false} />}
                </div>
                <div className="meta">
                  {ct.role && <span>{ct.role}</span>}
                  {ct.email && <span>{ct.email}</span>}
                  {ct.whatsapp && <span>WhatsApp {phoneMask(ct.whatsapp)}</span>}
                  {ct.purposes.map((p) => (
                    <span key={p} className="badge">
                      {PURPOSE_LABEL[p]}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
      {contactDialog && <ContactDialog clientId={c.id} contact={contactDialog === 'new' ? null : contactDialog} onClose={() => setContactDialog(null)} />}
    </>
  );
}

function ContactDialog({ clientId, contact, onClose }: { clientId: string; contact: Contact | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: contact?.name ?? '',
    role: contact?.role ?? '',
    email: contact?.email ?? '',
    whatsapp: contact?.whatsapp ?? '',
    purposes: contact?.purposes ?? ([] as ContactPurpose[]),
    active: contact?.active ?? true,
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['client', clientId] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => (contact ? api.put(`/contacts/${contact.id}`, { ...form, version: contact.version }) : api.post(`/clients/${clientId}/contacts`, form)),
    onSuccess: done,
  });
  const del = useMutation({ mutationFn: () => api.del(`/contacts/${contact!.id}`), onSuccess: done });
  const err = save.error;

  return (
    <Dialog title={contact ? 'Editar contato' : 'Novo contato'} onClose={onClose}>
      <form
        className="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <ErrorAlert error={err ?? del.error} />
        <Field label="Nome" required error={fieldError(err, 'name')}>
          {(p) => <input {...p} value={form.name} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <Field label="Cargo">{(p) => <input {...p} value={form.role} onChange={(e) => set('role', e.target.value)} />}</Field>
        <div className="form-row">
          <Field label="E-mail" error={fieldError(err, 'email')}>
            {(p) => <input {...p} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />}
          </Field>
          <Field label="WhatsApp" hint="Com DDD." error={fieldError(err, 'whatsapp')}>
            {(p) => <input {...p} type="tel" inputMode="tel" value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} />}
          </Field>
        </div>
        <fieldset>
          <legend>Recebe</legend>
          <div className="checks">
            {(Object.keys(PURPOSE_LABEL) as ContactPurpose[]).map((p) => (
              <label key={p} className="check">
                <input
                  type="checkbox"
                  checked={form.purposes.includes(p)}
                  onChange={(e) => set('purposes', e.target.checked ? [...form.purposes, p] : form.purposes.filter((x) => x !== p))}
                />
                {PURPOSE_LABEL[p]}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="check">
          <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Contato ativo
        </label>
        <div className="actions">
          <button className="btn btn-primary" disabled={save.isPending || !form.name}>
            Salvar
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          {contact && (
            <button type="button" className="btn btn-danger" onClick={() => window.confirm('Excluir este contato?') && del.mutate()}>
              Excluir
            </button>
          )}
        </div>
      </form>
    </Dialog>
  );
}

export function ClientFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const existing = useQuery({ queryKey: ['client', id], queryFn: () => api.get<ClientDetail>(`/clients/${id}`), enabled: editing });
  const [form, setForm] = useState({ code: '', name: '', tradeName: '', document: '', address: '', notes: '', active: true });
  const [duplicates, setDuplicates] = useState<{ code: string; name: string }[] | null>(null);

  useEffect(() => {
    const c = existing.data;
    if (c)
      setForm({
        code: c.code,
        name: c.name,
        tradeName: c.tradeName ?? '',
        document: documentMask(c.documentType, c.document).replace('—', ''),
        address: c.address ?? '',
        notes: c.notes ?? '',
        active: c.active,
      });
  }, [existing.data]);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const m = useMutation({
    mutationFn: async (confirmDuplicate: boolean) => {
      const body = { ...form, code: form.code || undefined, confirmDuplicate };
      if (editing) {
        await api.put(`/clients/${id}`, { ...body, version: existing.data!.version });
        return id!;
      }
      return (await api.post<{ id: string }>('/clients', body)).id;
    },
    onSuccess: (cid) => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['client', cid] });
      navigate(`/cadastros/clientes/${cid}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'possible_duplicate') setDuplicates((e.details as { matches: { code: string; name: string }[] }).matches);
    },
  });

  if (editing && existing.isLoading) return <Loading />;
  const err = m.error instanceof ApiError && m.error.code === 'possible_duplicate' ? null : m.error;

  function submit(e: FormEvent) {
    e.preventDefault();
    setDuplicates(null);
    m.mutate(false);
  }

  return (
    <>
      <div className="page-head">
        <h1>{editing ? 'Editar cliente' : 'Novo cliente'}</h1>
      </div>
      <form className="card form" onSubmit={submit} noValidate style={{ maxWidth: 860 }}>
        <ErrorAlert error={err} />
        {duplicates && (
          <div className="alert alert-warning" role="alert">
            <strong>Possível cliente duplicado</strong>
            Já existe cliente com este CPF/CNPJ: {duplicates.map((d) => `${d.code} — ${d.name}`).join('; ')}.
            <div className="actions" style={{ marginTop: '0.5rem' }}>
              <button type="button" className="btn btn-sm" onClick={() => m.mutate(true)}>
                Salvar mesmo assim
              </button>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setDuplicates(null)}>
                Revisar
              </button>
            </div>
          </div>
        )}
        <Field label="Nome / razão social" required error={fieldError(err, 'name')}>
          {(p) => <input {...p} value={form.name} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <div className="form-row">
          <Field label="Nome fantasia">{(p) => <input {...p} value={form.tradeName} onChange={(e) => set('tradeName', e.target.value)} />}</Field>
          <Field label="CPF/CNPJ" hint="Opcional. Validamos os dígitos." error={fieldError(err, 'document')}>
            {(p) => <input {...p} inputMode="numeric" value={form.document} onChange={(e) => set('document', e.target.value)} />}
          </Field>
        </div>
        <Field label="Código" hint={editing ? undefined : 'Vazio = automático (CLI-0001).'} error={fieldError(err, 'code')}>
          {(p) => <input {...p} value={form.code} onChange={(e) => set('code', e.target.value)} />}
        </Field>
        <Field label="Endereço">{(p) => <textarea {...p} value={form.address} onChange={(e) => set('address', e.target.value)} />}</Field>
        <Field label="Observações">{(p) => <textarea {...p} value={form.notes} onChange={(e) => set('notes', e.target.value)} />}</Field>
        {editing && (
          <label className="check">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Cliente ativo
          </label>
        )}
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending || !form.name}>
            {m.isPending ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="btn" onClick={() => navigate(-1)}>
            Cancelar
          </button>
        </div>
      </form>
    </>
  );
}

// ------------------------------------------------------------------ consultores

export function ConsultantsPage() {
  const [editing, setEditing] = useState<Consultant | 'new' | null>(null);
  const q = useQuery({ queryKey: ['consultants'], queryFn: () => api.get<{ data: Consultant[] }>('/consultants') });
  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/cadastros">← Cadastros</Link>
          </p>
          <h1>Consultores</h1>
          <p>Inativar um consultor preserva toda a produção histórica.</p>
        </div>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            Novo consultor
          </button>
        </div>
      </div>
      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="list">
          {q.data?.data.map((c) => (
            <button key={c.id} className="list-item" style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }} onClick={() => setEditing(c)}>
              <div className="title">
                {c.name} <ActiveBadge active={c.active} />
              </div>
              <div className="meta">
                {c.email && <span>{c.email}</span>}
                {c.phone && <span>{phoneMask(c.phone)}</span>}
                <span>{c.userId ? (c.userActive ? 'Com conta de acesso' : 'Conta inativa') : 'Sem conta de acesso'}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {editing && <ConsultantDialog consultant={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function ConsultantDialog({ consultant, onClose }: { consultant: Consultant | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: consultant?.name ?? '', email: consultant?.email ?? '', phone: consultant?.phone ?? '', active: consultant?.active ?? true });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const m = useMutation({
    mutationFn: () => (consultant ? api.put(`/consultants/${consultant.id}`, { ...form, version: consultant.version }) : api.post('/consultants', form)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['consultants'] });
      onClose();
    },
  });
  return (
    <Dialog title={consultant ? 'Editar consultor' : 'Novo consultor'} onClose={onClose}>
      <form
        className="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorAlert error={m.error} />
        <Field label="Nome" required error={fieldError(m.error, 'name')}>
          {(p) => <input {...p} value={form.name} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <div className="form-row">
          <Field label="E-mail de contato" error={fieldError(m.error, 'email')}>
            {(p) => <input {...p} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />}
          </Field>
          <Field label="Telefone" error={fieldError(m.error, 'phone')}>
            {(p) => <input {...p} type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} />}
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Consultor ativo
        </label>
        {!consultant && <p className="small muted">Depois de salvar, crie a conta de acesso em Configurações › Usuários.</p>}
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending || !form.name}>
            Salvar
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ------------------------------------------------------------------ cadastros auxiliares

const KIND_LABEL = { client: 'Com cliente', internal: 'Interno', personal: 'Pessoal' } as const;

export function LookupPage({ resource, title }: { resource: 'categories' | 'areas' | 'appointment-types'; title: string }) {
  const [editing, setEditing] = useState<Lookup | 'new' | null>(null);
  const q = useQuery({ queryKey: [resource], queryFn: () => api.get<{ data: Lookup[] }>(`/${resource}`) });
  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/cadastros">← Cadastros</Link>
          </p>
          <h1>{title}</h1>
          <p>Itens já utilizados devem ser inativados, não excluídos.</p>
        </div>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            Novo
          </button>
        </div>
      </div>
      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="list">
          {q.data?.data.map((l) => (
            <button key={l.id} className="list-item" style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }} onClick={() => setEditing(l)}>
              <div className="title">
                {l.name} <ActiveBadge active={l.active} /> {l.kind && <span className="badge">{KIND_LABEL[l.kind]}</span>}
              </div>
              {l.description && <div className="meta">{l.description}</div>}
            </button>
          ))}
        </div>
      )}
      {editing && <LookupDialog resource={resource} item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function LookupDialog({ resource, item, onClose }: { resource: string; item: Lookup | null; onClose: () => void }) {
  const qc = useQueryClient();
  const hasKind = resource === 'appointment-types';
  const [form, setForm] = useState({ name: item?.name ?? '', description: item?.description ?? '', active: item?.active ?? true, kind: item?.kind ?? 'client' });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const done = () => {
    void qc.invalidateQueries({ queryKey: [resource] });
    onClose();
  };
  const body = () => (hasKind ? form : { name: form.name, description: form.description, active: form.active });
  const save = useMutation({
    mutationFn: () => (item ? api.put(`/${resource}/${item.id}`, { ...body(), version: item.version }) : api.post(`/${resource}`, body())),
    onSuccess: done,
  });
  const del = useMutation({ mutationFn: () => api.del(`/${resource}/${item!.id}`), onSuccess: done });
  return (
    <Dialog title={item ? 'Editar' : 'Novo'} onClose={onClose}>
      <form
        className="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <ErrorAlert error={save.error ?? del.error} />
        <Field label="Nome" required error={fieldError(save.error, 'name')}>
          {(p) => <input {...p} value={form.name} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        {hasKind && (
          <Field label="Natureza" hint="Pessoal e interno não exigem cliente nem contrato.">
            {(p) => (
              <select {...p} value={form.kind} onChange={(e) => set('kind', e.target.value as typeof form.kind)}>
                {Object.entries(KIND_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        <Field label="Descrição">{(p) => <textarea {...p} value={form.description} onChange={(e) => set('description', e.target.value)} />}</Field>
        <label className="check">
          <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Ativo
        </label>
        <div className="actions">
          <button className="btn btn-primary" disabled={save.isPending || !form.name}>
            Salvar
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          {item && (
            <button type="button" className="btn btn-danger" onClick={() => window.confirm('Excluir definitivamente?') && del.mutate()}>
              Excluir
            </button>
          )}
        </div>
      </form>
    </Dialog>
  );
}

// Clientes vêm do Horizonte (financeiro) quando a integração está ativa.
function FinanceClientsNotice() {
  const me = useMe();
  const qc = useQueryClient();
  const st = useQuery({ queryKey: ['finance-status'], queryFn: () => api.get<{ enabled: boolean; linkedClients: number; lastClientSync: string | null }>('/finance/status') });
  const unlinked = useQuery({
    queryKey: ['finance-unlinked'],
    queryFn: () => api.get<{ data: { id: string; code: string; name: string }[] }>('/finance/clients/unlinked'),
    enabled: Boolean(st.data?.enabled),
  });
  const sync = useMutation({
    mutationFn: () => api.post<{ total: number; criados: number; atualizados: number; vinculados: number }>('/finance/clients/sync'),
    onSuccess: () => {
      for (const k of [['clients'], ['finance-status'], ['finance-unlinked']]) void qc.invalidateQueries({ queryKey: k });
    },
  });
  if (!st.data?.enabled) return null;
  const missing = unlinked.data?.data ?? [];
  return (
    <div className="alert alert-info small">
      <strong>Clientes vêm do Horizonte</strong>
      Cadastre e altere clientes no Horizonte; a lista daqui é atualizada a cada 15 minutos. {st.data.linkedClients} cliente(s) vinculado(s)
      {st.data.lastClientSync ? `, última sincronização em ${new Date(st.data.lastClientSync).toLocaleString('pt-BR')}` : ''}.
      {missing.length > 0 && (
        <div style={{ marginTop: '0.35rem' }}>
          Só no Gestão (cobranças deles não podem ser enviadas até existirem no Horizonte): {missing.map((c) => c.name).join(', ')}.
        </div>
      )}
      {me.isAdmin && (
        <div className="actions" style={{ marginTop: '0.5rem' }}>
          <button className="btn btn-sm" disabled={sync.isPending} onClick={() => sync.mutate()}>
            {sync.isPending ? 'Sincronizando…' : 'Sincronizar agora'}
          </button>
          {sync.data && (
            <span>
              {sync.data.criados} criado(s), {sync.data.atualizados} atualizado(s), {sync.data.vinculados} vinculado(s).
            </span>
          )}
        </div>
      )}
      <ErrorAlert error={sync.error} />
    </div>
  );
}
